import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import manufacturerService from '../../../services/manufacturerService';

const ManufacturersTab = () => {
  const [manufacturers, setManufacturers] = useState([]);
  const [newManufacturer, setNewManufacturer] = useState({ nazwa: '', opis: '', aktywny: true });
  const [editingManufacturer, setEditingManufacturer] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadManufacturers = useCallback(async () => {
    try {
      const data = await manufacturerService.getManufacturers();
      setManufacturers(data || []);
    } catch (err) {
      console.error('Błąd podczas pobierania producentów:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadManufacturers();
      setLoading(false);
    };
    init();
  }, [loadManufacturers]);

  const addManufacturer = async () => {
    if (!newManufacturer.nazwa.trim()) {
      toast.error('Nazwa producenta jest wymagana');
      return;
    }
    try {
      await manufacturerService.addManufacturer(newManufacturer);
      setNewManufacturer({ nazwa: '', opis: '', aktywny: true });
      loadManufacturers();
      toast.success('Producent został dodany pomyślnie');
    } catch (err) {
      console.error('Błąd podczas dodawania producenta:', err);
      toast.error(`Błąd podczas dodawania producenta: ${err.message}`);
    }
  };

  const deleteManufacturer = async (manufacturerId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego producenta?')) return;
    try {
      await manufacturerService.deleteManufacturer(manufacturerId);
      loadManufacturers();
      toast.success('Producent został usunięty pomyślnie');
    } catch (err) {
      console.error('Błąd podczas usuwania producenta:', err);
      toast.error(err.message || 'Błąd podczas usuwania producenta');
    }
  };

  const startEditManufacturer = (manufacturer) => {
    setEditingManufacturer({
      id: manufacturer.id,
      nazwa: manufacturer.nazwa,
      opis: manufacturer.opis || '',
      aktywny: manufacturer.aktywny
    });
  };

  const cancelEditManufacturer = () => {
    setEditingManufacturer(null);
  };

  const updateManufacturer = async () => {
    if (!editingManufacturer?.nazwa?.trim()) {
      toast.error('Nazwa producenta jest wymagana');
      return;
    }
    try {
      await manufacturerService.updateManufacturer(editingManufacturer.id, {
        nazwa: editingManufacturer.nazwa,
        opis: editingManufacturer.opis || '',
        aktywny: editingManufacturer.aktywny
      });
      loadManufacturers();
      setEditingManufacturer(null);
      toast.success('Producent został zaktualizowany pomyślnie');
    } catch (err) {
      console.error('Błąd podczas aktualizacji producenta:', err);
      toast.error(err.message || 'Błąd podczas aktualizacji producenta');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid var(--border)',
          borderTop: '3px solid var(--info)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="admin-table-wrap" style={{ maxWidth: '800px' }}>
      <style>{`
        @media (max-width: 600px) {
          .admin-form-row { grid-template-columns: 1fr !important; }
          .manuf-list-row { flex-direction: column !important; align-items: stretch !important; gap: 0.4rem !important; }
          .manuf-list-row > div { width: 100%; }
        }
        @media (min-width: 601px) and (max-width: 900px) {
          .admin-form-row { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
      {/* Formularz dodawania */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '5px',
        marginBottom: '1rem',
        padding: '0.75rem',
        background: 'var(--bg)'
      }}>
        <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
          ➕ Nowy Producent
        </h6>
        <div className="admin-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '0.5rem', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '2px' }}>
              Nazwa *
            </label>
            <input
              type="text"
              value={newManufacturer.nazwa}
              onChange={(e) => setNewManufacturer({ ...newManufacturer, nazwa: e.target.value })}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px', height: '28px' }}
              placeholder="np. ALLNUTRITION"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '2px' }}>
              Opis
            </label>
            <input
              type="text"
              value={newManufacturer?.opis || ''}
              onChange={(e) => setNewManufacturer({ ...newManufacturer, opis: e.target.value })}
              style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '12px', height: '28px' }}
              placeholder="Opis (opcjonalnie)"
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', height: '28px' }}>
            <input
              type="checkbox"
              checked={newManufacturer.aktywny}
              onChange={(e) => setNewManufacturer({ ...newManufacturer, aktywny: e.target.checked })}
            />
            Aktywny
          </label>
          <button
            onClick={() => { addManufacturer(); }}
            style={{ padding: '4px 10px', border: '1px solid var(--success)', color: 'var(--success)', fontSize: '11px', fontWeight: '500', height: '28px' }}
          >
            Dodaj
          </button>
        </div>
      </div>

      {/* Lista */}
      <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{ backgroundColor: 'var(--border)', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1rem' }}>🏭</span>
          <h6 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>Lista Producentów ({manufacturers.length})</h6>
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {manufacturers.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', margin: 0, padding: '1rem', fontSize: '12px' }}>Brak producentów</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {manufacturers.map((manufacturer) => (
                <div
                  key={manufacturer.id}
                  className="manuf-list-row"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: manufacturer.aktywny ? 'var(--card-bg)' : 'var(--warning-light)',
                    borderBottom: '1px solid var(--bg)',
                    fontSize: '12px'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    {editingManufacturer && editingManufacturer.id === manufacturer.id ? (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="text" value={editingManufacturer?.nazwa || ''} onChange={(e) => setEditingManufacturer({ ...editingManufacturer, nazwa: e.target.value })}
                          style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px', flex: '1 1 150px', minWidth: 0 }} placeholder="Nazwa" />
                        <input type="text" value={editingManufacturer?.opis || ''} onChange={(e) => setEditingManufacturer({ ...editingManufacturer, opis: e.target.value })}
                          style={{ padding: '4px 6px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px', flex: '1 1 150px', minWidth: 0 }} placeholder="Opis" />
                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px' }}>
                          <input type="checkbox" checked={editingManufacturer?.aktywny || false} onChange={(e) => setEditingManufacturer({ ...editingManufacturer, aktywny: e.target.checked })} />
                          Aktywny
                        </label>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: '500', color: manufacturer.aktywny ? 'var(--text-secondary)' : 'var(--warning)' }}>{manufacturer.nazwa}</span>
                        {!manufacturer.aktywny && <span style={{ fontSize: '10px', color: 'var(--warning)' }}>(nieaktywny)</span>}
                        {manufacturer.opis && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>- {manufacturer.opis}</span>}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {editingManufacturer && editingManufacturer.id === manufacturer.id ? (
                      <>
                        <button onClick={updateManufacturer} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)' }}>Zapisz</button>
                        <button onClick={cancelEditManufacturer} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}>Anuluj</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEditManufacturer(manufacturer)} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' }}>Edytuj</button>
                        <button onClick={() => deleteManufacturer(manufacturer.id)} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' }}>Usuń</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManufacturersTab;
